import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useAuth } from '@/context/AuthContext';
import api from '../../../system/client/src/services/api';
import PageShell from '../../Components/PageShell';
import Modal from '../../Components/Modal';
import Spinner from '../../Components/Spinner';
import { customsEnabled } from '../Traders/customsTreatments';
import './ProfilePage.css';

const initials = (firstName = '', lastName = '') =>
    `${(firstName[0] || '').toUpperCase()}${(lastName[0] || '').toUpperCase()}` || '?';

const ProfilePage = () => {
    const { user, updateUser } = useAuth();

    const [profile, setProfile] = useState({
        firstName: '',
        lastName: '',
        email: '',
    });
    const [savingProfile, setSavingProfile] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const [teamMembers, setTeamMembers] = useState([]);
    const [teamLoading, setTeamLoading] = useState(false);

    const [addOpen, setAddOpen] = useState(false);
    const [addForm, setAddForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
    });
    const [addSaving, setAddSaving] = useState(false);

    const [confirmRemove, setConfirmRemove] = useState(null);
    // Org setting being confirmed: true = turning customs on, false = off.
    const [confirmCustoms, setConfirmCustoms] = useState(null);
    const [savingCustoms, setSavingCustoms] = useState(false);

    const [resetTarget, setResetTarget] = useState(null);
    const [resetPassword, setResetPassword] = useState('');
    const [resetSaving, setResetSaving] = useState(false);

    const [pwForm, setPwForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [pwSaving, setPwSaving] = useState(false);
    const [pwOpen, setPwOpen] = useState(false);

    const openChangePassword = () => {
        setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPwOpen(true);
    };

    useEffect(() => {
        if (!user) return;
        setProfile({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
        });
    }, [user]);

    const fetchTeamMembers = async () => {
        try {
            setTeamLoading(true);
            const data = await api.get('team/members');
            setTeamMembers(data || []);
        } catch (e) {
            console.error('Error fetching team members:', e);
        } finally {
            setTeamLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === 'owner') fetchTeamMembers();
    }, [user?.role]);

    const openEdit = () => {
        setProfile({
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
        });
        setEditOpen(true);
    };

    const handleProfileSubmit = async (e) => {
        e?.preventDefault?.();
        if (!profile.firstName.trim() || !profile.lastName.trim()) {
            message.error('First and last name required');
            return;
        }
        if (!profile.email.trim()) {
            message.error('Email required');
            return;
        }
        try {
            setSavingProfile(true);
            const updated = await api.put('profile', {
                firstName: profile.firstName.trim(),
                lastName: profile.lastName.trim(),
                email: profile.email.trim(),
            });
            await updateUser(updated);
            message.success('Profile updated');
            setEditOpen(false);
        } catch (err) {
            console.error('Error updating profile:', err);
            message.error('Failed to update profile');
        } finally {
            setSavingProfile(false);
        }
    };

    const handleAddMember = async (e) => {
        e?.preventDefault?.();
        if (!addForm.firstName.trim() || !addForm.lastName.trim()) {
            message.error('First and last name required');
            return;
        }
        if (!addForm.email.trim()) {
            message.error('Email required');
            return;
        }
        if (addForm.password.length < 6) {
            message.error('Password must be at least 6 characters');
            return;
        }
        try {
            setAddSaving(true);
            await api.post('team/add-member', {
                firstName: addForm.firstName.trim(),
                lastName: addForm.lastName.trim(),
                email: addForm.email.trim(),
                password: addForm.password,
            });
            message.success('Team member added');
            setAddOpen(false);
            setAddForm({ firstName: '', lastName: '', email: '', password: '' });
            fetchTeamMembers();
        } catch (err) {
            console.error('Error adding member:', err);
            if (err.response?.status === 402) {
                // Plan seat limit — the global UpgradeModal also fires; close the
                // add form so the upgrade prompt isn't buried behind it.
                setAddOpen(false);
            } else {
                message.error(err.response?.data?.message || 'Failed to add team member');
            }
        } finally {
            setAddSaving(false);
        }
    };

    const handleCustomsSetting = async () => {
        if (confirmCustoms === null) return;
        setSavingCustoms(true);
        try {
            const { organization } = await api.put('organization/settings', {
                customs_profile_enabled: confirmCustoms,
            });
            updateUser({ organization });
            message.success(confirmCustoms ? 'Border information turned on' : 'Border information turned off');
            setConfirmCustoms(null);
        } catch (err) {
            console.error('Error updating organisation settings:', err);
            message.error(err?.response?.data?.message || 'Could not change the setting');
        } finally {
            setSavingCustoms(false);
        }
    };

    const handleRemove = async () => {
        if (!confirmRemove) return;
        try {
            await api.delete(`team/members/${confirmRemove.user_id}`);
            message.success('Member removed');
            setConfirmRemove(null);
            fetchTeamMembers();
        } catch (err) {
            console.error('Error removing member:', err);
            message.error('Failed to remove member');
        }
    };

    const openReset = (member) => {
        setResetTarget(member);
        setResetPassword('');
    };

    const handleResetPassword = async (e) => {
        e?.preventDefault?.();
        if (!resetTarget) return;
        if (resetPassword.length < 6) {
            message.error('Password must be at least 6 characters');
            return;
        }
        try {
            setResetSaving(true);
            await api.post(`team/members/${resetTarget.user_id}/reset-password`, {
                password: resetPassword,
            });
            message.success('Password reset');
            setResetTarget(null);
            setResetPassword('');
        } catch (err) {
            console.error('Error resetting password:', err);
            message.error('Failed to reset password');
        } finally {
            setResetSaving(false);
        }
    };

    const handleChangePassword = async (e) => {
        e?.preventDefault?.();
        if (!pwForm.currentPassword) {
            message.error('Enter your current password');
            return;
        }
        if (pwForm.newPassword.length < 8) {
            message.error('New password must be at least 8 characters');
            return;
        }
        if (pwForm.newPassword !== pwForm.confirmPassword) {
            message.error('New passwords do not match');
            return;
        }
        try {
            setPwSaving(true);
            await api.put('profile/password', {
                currentPassword: pwForm.currentPassword,
                newPassword: pwForm.newPassword,
            });
            message.success('Password changed.');
            setPwForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });
            setPwOpen(false);
        } catch (err) {
            console.error('Error changing password:', err);
            message.error(
                err.response?.data?.message || 'Could not change password.'
            );
        } finally {
            setPwSaving(false);
        }
    };

    const isOwner = user?.role === 'owner';

    return (
        <PageShell>
            <div className="page-head">
                <span className="kicker">Settings</span>
                <h1>
                    Your <span className="accent">account.</span>
                </h1>
                <p>Manage your profile and your team's access to FreightSource.</p>
            </div>

            <div className="identity">
                <span className="avatar">
                    {initials(user?.firstName, user?.lastName)}
                </span>
                <div className="who">
                    <span className="name">
                        {user?.firstName} {user?.lastName}
                    </span>
                    <span className="org">{user?.organization?.name || '—'}</span>
                    <div className="meta">
                        <span className={`badge ${isOwner ? 'hot' : 'cold'}`}>
                            {isOwner ? 'Owner' : 'Member'}
                        </span>
                        <span
                            style={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: 10,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: 'var(--muted)',
                            }}
                        >
                            {user?.email}
                        </span>
                    </div>
                </div>
                <div className="actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={async () => {
                            try {
                                await api.post('profile/onboarding-reset');
                            } catch (err) {
                                console.error('Replay reset failed:', err);
                            }
                            updateUser({ onboarding_completed_at: null });
                            window.dispatchEvent(new CustomEvent('onboarding:replay'));
                        }}
                    >
                        Replay tour
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={openEdit}
                    >
                        Edit profile
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={openChangePassword}
                    >
                        Change password
                    </button>
                </div>
            </div>

            <section className="section">
                <div className="section-head">
                    <div>
                        <span className="kicker">Organisation</span>
                        <h2>Features.</h2>
                    </div>
                </div>
                <div className="setting-row">
                    <div className="setting-text">
                        <strong>Might need at the border</strong>
                        <span>
                            Shows which border treatments a trader's goods might need — port health, CITES,
                            licences, excise, anti-dumping, duty — as a card on each trader, a column in trader
                            lists and a search filter. From the HMRC tariff.
                        </span>
                        {!isOwner && <span className="setting-note">Only the organisation owner can change this.</span>}
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={customsEnabled(user)}
                        aria-label="Might need at the border"
                        className={`toggle-switch${customsEnabled(user) ? ' on' : ''}`}
                        disabled={!isOwner}
                        onClick={() => setConfirmCustoms(!customsEnabled(user))}
                    >
                        <span className="toggle-knob" />
                        <span className="toggle-text">{customsEnabled(user) ? 'On' : 'Off'}</span>
                    </button>
                </div>
            </section>

            {isOwner && (
                <section className="section">
                    <div className="section-head">
                        <div>
                            <span className="kicker">Team</span>
                            <h2>
                                {teamMembers.length} member
                                {teamMembers.length === 1 ? '' : 's'}.
                            </h2>
                        </div>
                        <button
                            type="button"
                            data-onboard="add-member"
                            className="btn btn-primary"
                            onClick={() => setAddOpen(true)}
                        >
                            Add member <span className="arrow">→</span>
                        </button>
                    </div>

                    {teamLoading ? (
                        <Spinner block label="Loading team" />
                    ) : teamMembers.length === 0 ? (
                        <div className="empty">
                            <span className="kicker">Empty</span>
                            <p>You haven't added any team members yet.</p>
                        </div>
                    ) : (
                        <>
                            <div className="team-table-wrap">
                                <table className="fs-table">
                                    <thead>
                                        <tr>
                                            <th>User</th>
                                            <th style={{ width: 110 }}>Role</th>
                                            <th style={{ width: 140 }}>Joined</th>
                                            <th
                                                style={{
                                                    width: 100,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {teamMembers.map((m) => {
                                            const owner = m.role === 'owner';
                                            return (
                                                <tr key={m.user_id}>
                                                    <td className="co">
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 12,
                                                            }}
                                                        >
                                                            <span className="avatar sm">
                                                                {initials(
                                                                    m.first_name,
                                                                    m.last_name,
                                                                )}
                                                            </span>
                                                            <span>
                                                                {m.first_name}{' '}
                                                                {m.last_name}
                                                                <small>
                                                                    {m.email}
                                                                </small>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`badge ${owner ? 'hot' : 'cold'}`}
                                                        >
                                                            {owner ? 'Owner' : 'Member'}
                                                        </span>
                                                    </td>
                                                    <td className="date">
                                                        {m.created_at
                                                            ? new Date(
                                                                  m.created_at,
                                                              ).toLocaleDateString(
                                                                  'en-GB',
                                                                  {
                                                                      day: '2-digit',
                                                                      month: 'short',
                                                                      year: 'numeric',
                                                                  },
                                                              )
                                                            : '—'}
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {!owner && (
                                                            <span
                                                                style={{
                                                                    display:
                                                                        'inline-flex',
                                                                    gap: 4,
                                                                    justifyContent:
                                                                        'flex-end',
                                                                }}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="link-action"
                                                                    onClick={() =>
                                                                        openReset(m)
                                                                    }
                                                                >
                                                                    Reset
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="link-danger"
                                                                    onClick={() =>
                                                                        setConfirmRemove(m)
                                                                    }
                                                                >
                                                                    Remove
                                                                </button>
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="team-cards">
                                {teamMembers.map((m) => {
                                    const owner = m.role === 'owner';
                                    return (
                                        <div key={m.user_id} className="trader-card">
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 12,
                                                }}
                                            >
                                                <span className="avatar sm">
                                                    {initials(
                                                        m.first_name,
                                                        m.last_name,
                                                    )}
                                                </span>
                                                <div>
                                                    <div className="co">
                                                        {m.first_name} {m.last_name}
                                                    </div>
                                                    <div className="meta">
                                                        {m.email}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <span
                                                    className={`badge ${owner ? 'hot' : 'cold'}`}
                                                >
                                                    {owner ? 'Owner' : 'Member'}
                                                </span>
                                                {!owner && (
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            gap: 4,
                                                        }}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="link-action"
                                                            onClick={() =>
                                                                openReset(m)
                                                            }
                                                        >
                                                            Reset
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="link-danger"
                                                            onClick={() =>
                                                                setConfirmRemove(m)
                                                            }
                                                        >
                                                            Remove
                                                        </button>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </section>
            )}

            <Modal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                kicker="Profile"
                title="Edit your details"
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setEditOpen(false)}
                            disabled={savingProfile}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleProfileSubmit}
                            disabled={savingProfile}
                        >
                            {savingProfile ? 'Saving…' : 'Save changes'}
                        </button>
                    </>
                }
            >
                <form onSubmit={handleProfileSubmit} noValidate>
                    <div className="profile-grid">
                        <div className="form-field">
                            <label htmlFor="firstName">First name</label>
                            <input
                                id="firstName"
                                type="text"
                                autoComplete="given-name"
                                required
                                value={profile.firstName}
                                onChange={(e) =>
                                    setProfile((p) => ({
                                        ...p,
                                        firstName: e.target.value,
                                    }))
                                }
                                autoFocus
                            />
                        </div>
                        <div className="form-field">
                            <label htmlFor="lastName">Last name</label>
                            <input
                                id="lastName"
                                type="text"
                                autoComplete="family-name"
                                required
                                value={profile.lastName}
                                onChange={(e) =>
                                    setProfile((p) => ({
                                        ...p,
                                        lastName: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>
                    <div className="form-field" style={{ marginBottom: 0 }}>
                        <label htmlFor="email">Email</label>
                        <input
                            id="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={profile.email}
                            onChange={(e) =>
                                setProfile((p) => ({
                                    ...p,
                                    email: e.target.value,
                                }))
                            }
                        />
                    </div>
                </form>
            </Modal>

            <Modal
                open={pwOpen}
                onClose={() => setPwOpen(false)}
                kicker="Security"
                title="Change password"
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setPwOpen(false)}
                            disabled={pwSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleChangePassword}
                            disabled={pwSaving}
                        >
                            {pwSaving ? 'Saving…' : 'Change password'}
                        </button>
                    </>
                }
            >
                <form onSubmit={handleChangePassword} noValidate>
                    <div className="form-field">
                        <label htmlFor="current-password">
                            Current password
                        </label>
                        <input
                            id="current-password"
                            type="password"
                            autoComplete="current-password"
                            value={pwForm.currentPassword}
                            onChange={(e) =>
                                setPwForm((f) => ({
                                    ...f,
                                    currentPassword: e.target.value,
                                }))
                            }
                            autoFocus
                        />
                    </div>
                    <div className="form-field">
                        <label htmlFor="new-password">New password</label>
                        <input
                            id="new-password"
                            type="password"
                            autoComplete="new-password"
                            value={pwForm.newPassword}
                            onChange={(e) =>
                                setPwForm((f) => ({
                                    ...f,
                                    newPassword: e.target.value,
                                }))
                            }
                            placeholder="At least 8 characters"
                        />
                    </div>
                    <div className="form-field" style={{ marginBottom: 0 }}>
                        <label htmlFor="confirm-password">
                            Confirm new password
                        </label>
                        <input
                            id="confirm-password"
                            type="password"
                            autoComplete="new-password"
                            value={pwForm.confirmPassword}
                            onChange={(e) =>
                                setPwForm((f) => ({
                                    ...f,
                                    confirmPassword: e.target.value,
                                }))
                            }
                        />
                    </div>
                </form>
            </Modal>

            <Modal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                kicker="Team"
                title="Add a member"
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setAddOpen(false)}
                            disabled={addSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleAddMember}
                            disabled={addSaving}
                        >
                            {addSaving ? 'Adding…' : 'Add member'}
                        </button>
                    </>
                }
            >
                <form onSubmit={handleAddMember} noValidate>
                    <div className="profile-grid">
                        <div className="form-field">
                            <label htmlFor="add-first">First name</label>
                            <input
                                id="add-first"
                                type="text"
                                value={addForm.firstName}
                                onChange={(e) =>
                                    setAddForm((f) => ({
                                        ...f,
                                        firstName: e.target.value,
                                    }))
                                }
                                autoFocus
                            />
                        </div>
                        <div className="form-field">
                            <label htmlFor="add-last">Last name</label>
                            <input
                                id="add-last"
                                type="text"
                                value={addForm.lastName}
                                onChange={(e) =>
                                    setAddForm((f) => ({
                                        ...f,
                                        lastName: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>
                    <div className="form-field">
                        <label htmlFor="add-email">Email</label>
                        <input
                            id="add-email"
                            type="email"
                            value={addForm.email}
                            onChange={(e) =>
                                setAddForm((f) => ({ ...f, email: e.target.value }))
                            }
                        />
                    </div>
                    <div className="form-field" style={{ marginBottom: 0 }}>
                        <label htmlFor="add-password">Temporary password</label>
                        <input
                            id="add-password"
                            type="password"
                            value={addForm.password}
                            onChange={(e) =>
                                setAddForm((f) => ({
                                    ...f,
                                    password: e.target.value,
                                }))
                            }
                            placeholder="Min 6 characters"
                        />
                    </div>
                </form>
            </Modal>

            <Modal
                open={confirmCustoms !== null}
                onClose={() => setConfirmCustoms(null)}
                kicker="Confirm"
                title={confirmCustoms ? 'Turn border information on?' : 'Turn border information off?'}
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setConfirmCustoms(null)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={`btn btn-primary${confirmCustoms ? '' : ' danger'}`}
                            onClick={handleCustomsSetting}
                            disabled={savingCustoms}
                        >
                            {confirmCustoms ? 'Turn on' : 'Turn off'}
                        </button>
                    </>
                }
            >
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)' }}>
                    {confirmCustoms
                        ? 'Everyone in your organisation will see what traders\' goods might need at the border: the card on each trader, the column in trader lists and the search filter.'
                        : 'This hides the border information for everyone in your organisation: the card on each trader, the column in trader lists and the search filter. You can turn it back on here at any time.'}
                </p>
            </Modal>

            <Modal
                open={Boolean(confirmRemove)}
                onClose={() => setConfirmRemove(null)}
                kicker="Confirm"
                title="Remove team member?"
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setConfirmRemove(null)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary danger"
                            onClick={handleRemove}
                        >
                            Remove
                        </button>
                    </>
                }
            >
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)' }}>
                    {confirmRemove
                        ? `${confirmRemove.first_name} ${confirmRemove.last_name} will lose access immediately.`
                        : ''}
                </p>
            </Modal>

            <Modal
                open={Boolean(resetTarget)}
                onClose={() => setResetTarget(null)}
                kicker="Team"
                title="Reset password"
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setResetTarget(null)}
                            disabled={resetSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleResetPassword}
                            disabled={resetSaving}
                        >
                            {resetSaving ? 'Resetting…' : 'Reset password'}
                        </button>
                    </>
                }
            >
                <form onSubmit={handleResetPassword} noValidate>
                    <p
                        style={{
                            margin: '0 0 14px',
                            fontSize: 14,
                            color: 'var(--ink-2)',
                        }}
                    >
                        {resetTarget
                            ? `Set a new password for ${resetTarget.first_name} ${resetTarget.last_name}. Share it with them directly — they can change it once signed in.`
                            : ''}
                    </p>
                    <div className="form-field" style={{ marginBottom: 0 }}>
                        <label htmlFor="reset-password">New password</label>
                        <input
                            id="reset-password"
                            type="password"
                            value={resetPassword}
                            onChange={(e) => setResetPassword(e.target.value)}
                            placeholder="Min 6 characters"
                            autoFocus
                        />
                    </div>
                </form>
            </Modal>
        </PageShell>
    );
};

export default ProfilePage;
